import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { MdDashboard, MdPeople, MdEventNote, MdAttachMoney, MdInventory2, MdSettings } from 'react-icons/md';
import logoSistema from '../assets/logosistema.png';

const navItems = [
  { to: '/painel',      icon: <MdDashboard size={20} />,  label: 'Painel' },
  { to: '/pacientes',   icon: <MdPeople size={20} />,     label: 'Pacientes' },
  { to: '/agenda',      icon: <MdEventNote size={20} />,  label: 'Agenda' },
  { to: '/estoque',     icon: <MdInventory2 size={20} />, label: 'Estoque' },
  { to: '/financeiro',  icon: <MdAttachMoney size={20} />,label: 'Financeiro' },
];

const LogoutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
  </svg>
);

const Tooltip = ({ label }) => (
  <div style={{
    position: 'absolute',
    left: 'calc(100% + 12px)',
    top: '50%',
    transform: 'translateY(-50%)',
    background: '#1C1C1E',
    color: '#FFFFFF',
    padding: '7px 12px',
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 600,
    letterSpacing: '-0.005em',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    boxShadow: '0 4px 12px rgba(28,28,30,0.18)',
    zIndex: 100,
  }}>
    <div style={{
      position: 'absolute',
      left: -4,
      top: '50%',
      transform: 'translateY(-50%) rotate(45deg)',
      width: 8,
      height: 8,
      background: '#1C1C1E',
    }} />
    {label}
  </div>
);

function SidebarNavItem({ to, icon, label }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <NavLink
        to={to}
        style={({ isActive }) => ({
          width: 44,
          height: 44,
          borderRadius: 12,
          border: 'none',
          background: isActive
            ? 'linear-gradient(160deg, #9F4A60 0%, #7A3A4C 100%)'
            : hovered ? '#F5F5F7' : 'transparent',
          color: isActive ? '#FFFFFF' : '#1C1C1E',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.12s ease',
          boxShadow: isActive
            ? '0 1px 2px rgba(159,74,96,0.2), 0 6px 16px -8px rgba(159,74,96,0.4)'
            : 'none',
          textDecoration: 'none',
        })}
      >
        {icon}
      </NavLink>
      {hovered && <Tooltip label={label} />}
    </div>
  );
}

function LogoutButton({ onLogout }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={onLogout}
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          border: 'none',
          background: hovered ? '#FBE0E5' : 'transparent',
          color: '#9F4A60',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.12s ease',
        }}
      >
        <LogoutIcon />
      </button>
      {hovered && <Tooltip label="Sair" />}
    </div>
  );
}

export default function DashboardLayout({
  currentUser,
  clinicData,
  onClinicChange,
  onLogout,
  patients,
  onAddPatient,
  onUpdatePatient,
  onRefreshPatient,
  settings,
  onSettingsUpdate,
}) {
  return (
    <div className="dashboard-shell">
      <aside style={{
        background: '#FFFFFF',
        borderRight: '1px solid #E5E5EA',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px 0 16px',
        boxSizing: 'border-box',
        height: '100%',
        overflow: 'visible',
      }}>
        {/* Logo */}
        <div style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          background: 'linear-gradient(160deg, #FBE0E5 0%, #F4C4CE 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 2px rgba(159,74,96,0.18)',
          marginBottom: 28,
          flexShrink: 0,
          overflow: 'hidden',
        }}>
          <img
            src={settings?.logo || logoSistema}
            alt="Logo"
            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }}
          />
        </div>

        {/* Main nav */}
        <nav style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          flex: 1,
          alignItems: 'center',
        }}>
          {navItems.map((item) => (
            <SidebarNavItem key={item.to} to={item.to} icon={item.icon} label={item.label} />
          ))}
        </nav>

        {/* Bottom: config + logout */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          alignItems: 'center',
          paddingTop: 16,
          borderTop: '1px solid #F2F2F7',
          width: '100%',
        }}>
          <SidebarNavItem to="/configuracoes" icon={<MdSettings size={20} />} label="Configurações" />
          <LogoutButton onLogout={onLogout} />
        </div>
      </aside>

      <main className="dashboard-content">
        <Outlet context={{ currentUser, clinicData, onClinicChange, patients, onAddPatient, onUpdatePatient, onRefreshPatient, settings, onSettingsUpdate }} />
      </main>
    </div>
  );
}
